import { Outlet, useLocation } from 'react-router-dom';
import { useProgress } from '../contexts/ProgressContext';
import { useTenant } from '../FeatureFlags';
import { usePanelIdleTermination } from '../hooks/usePanelIdleTermination';
import { PANELS } from '../config/panels';
import { getInitialParams } from '../utils/initialParams';

function useRouteProgressWidth() {
  const { pathname } = useLocation();
  if (pathname.startsWith('/connect/signin')) return '66%';
  if (pathname.startsWith('/connect')) return '33%';
  return '0%';
}

export function Layout() {
  const routeWidth = useRouteProgressWidth();
  const { override } = useProgress();
  const tenant = useTenant();
  const progressWidth = override !== null ? `${override}%` : routeWidth;
  usePanelIdleTermination();

  const params = getInitialParams();
  const panel = PANELS.find((p) => params[p.param.toLowerCase()]);
  const headerLogo = panel?.logoUrl;
  const hideHeader =
    tenant.flow.chromeless || panel?.hideHeader || tenant.flow.hideHeader;

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
    >
      {/* Progress strip */}
      <div className="h-[3px] bg-rule-light flex-shrink-0">
        <div
          className="h-full bg-primary transition-[width] duration-[600ms] [transition-timing-function:cubic-bezier(0.16,1,0.3,1)]"
          style={{ width: progressWidth }}
        />
      </div>

      {/* Header */}
      {!hideHeader && (
        <header className="border-b border-rule-light bg-surface flex-shrink-0 px-5 sm:px-6">
          <div className="max-w-[1080px] mx-auto h-14 sm:h-16 flex items-center">
            {headerLogo && (
              <img
                src={headerLogo}
                alt={tenant.appName}
                className="h-7 sm:h-8 w-auto"
              />
            )}
          </div>
        </header>
      )}

      {/* Main Content */}
      <main className="flex-1 flex flex-col">
        <Outlet />
      </main>

      {/* Footer */}
      {!tenant.flow.chromeless && (
        <footer className="border-t border-rule-light bg-surface flex-shrink-0 px-5 sm:px-6">
          <div className="max-w-[1080px] mx-auto h-12 flex items-center justify-center gap-3 sm:gap-6 flex-wrap">
            <span className="flex items-center gap-1.5 text-[13px] text-ink-faint">
              <svg
                className="w-[14px] h-[14px]"
                style={{ color: 'var(--retailer-success)' }}
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                />
              </svg>
              Secured by{' '}
              <strong className="font-semibold text-ink-soft ml-1">Ario</strong>
            </span>
            <span className="text-rule">·</span>
            <a
              href="https://ariodata.com/corelens-privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[13px] text-ink-faint hover:text-ink-muted transition-colors"
            >
              Privacy Policy
            </a>
          </div>
        </footer>
      )}
    </div>
  );
}
